import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class MarkdownService {

  constructor(private _sanitizer: DomSanitizer) {}

  /**
   * Format message text - handles both HTML and markdown responses
   * Extracted from AI chatbot widget for reuse across the app
   */
  formatMessage(message: string): string {
    if (!message) { return ''; }

    // Check if the message contains HTML tags (likely already formatted)
    const hasHtmlTags = /<[^>]+>/g.test(message);

    if (hasHtmlTags) {
      // Message contains HTML - sanitize and return (preserving safe HTML tags)
      return this.sanitizeHtml(message);
    } else {
      // Message is markdown - convert to HTML
      return this.convertMarkdownToHtml(message);
    }
  }

  /**
   * Convert markdown to plain text for preview purposes
   */
  markdownToPlainText(message: string): string {
    if (!message) { return ''; }

    // Remove markdown formatting and convert to plain text
    const plainText = message
      // Remove markdown headers
      .replace(/^#{1,6}\s+(.+)$/gm, '$1')
      // Remove markdown links but keep the text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove bold formatting
      .replace(/\*\*(.*?)\*\*/g, '$1')
      // Remove italic formatting
      .replace(/\*([^*]+)\*/g, '$1')
      // Remove list markers
      .replace(/^[-*]\s+(.+)$/gm, '$1')
      .replace(/^\d+\.\s+(.+)$/gm, '$1')
      // Clean up multiple spaces and newlines
      .replace(/\s+/g, ' ')
      .trim();

    return plainText;
  }

  /**
   * Sanitize HTML via Angular's DomSanitizer. Strips event handlers, script
   * tags, javascript:/data: URLs, and any other attack surface — the
   * conservative built-in sanitizer is the right tool here. Previous
   * homegrown implementation parsed HTML through `innerHTML` on a detached
   * element which exposed an XSS surface for poisoned tags arriving via
   * AI tool output (e.g. `<td><img src=x onerror=...></td>`).
   */
  private sanitizeHtml(html: string): string {
    return this._sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
  }

  /**
   * Whitelist of URL schemes allowed in markdown links. Anything outside
   * this set (notably `javascript:`, `data:`, `vbscript:`) is replaced
   * with `#` so the link still renders but cannot navigate to a hostile
   * URL.
   */
  private readonly SAFE_LINK_SCHEME = /^(?:https?:|mailto:|tel:|\/|#)/i;

  private sanitizeUrl(url: string): string {
    return this.SAFE_LINK_SCHEME.test(url.trim()) ? url : '#';
  }

  /**
   * Convert markdown to HTML
   */
  private convertMarkdownToHtml(message: string): string {
    // Escape HTML to prevent XSS, but allow our formatting
    let formatted = message
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    // Convert markdown-style formatting to HTML
    formatted = formatted
      // Headers: ### -> <h3>, ## -> <h2>, # -> <h1> (process before other formatting)
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Markdown links: [text](url) -> <a href="url">text</a>. URL scheme
      // is validated against SAFE_LINK_SCHEME — anything else (e.g.
      // `javascript:alert(1)`) is rewritten to `#` so the link renders as
      // dead text rather than a hostile navigation target.
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        (_match, text: string, url: string) =>
          `<a href="${this.sanitizeUrl(url)}" target="_blank" rel="noopener noreferrer">${text}</a>`,
      )
      // Bold text: **text** -> <strong>text</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic text: *text* -> <em>text</em> (process after bold to avoid conflicts)
      .replace(/\*([^*]+)\*/g, '<em>$1</em>');

    // Process lists and paragraphs by splitting into lines first
    const lines = formatted.split('\n');
    const processedLines: string[] = [];
    let currentListType: 'unordered' | null = null;
    let currentListItems: string[] = [];
    let currentParagraphLines: string[] = [];
    let currentTableHeaders: string[] | null = null;
    let currentTableRows: string[][] = [];

    const flushCurrentList = () => {
      if (currentListItems.length > 0) {
        if (currentListType === 'unordered') {
          processedLines.push('<ul>' + currentListItems.join('') + '</ul>');
        }
        currentListItems = [];
        currentListType = null;
      }
    };

    const flushCurrentParagraph = () => {
      if (currentParagraphLines.length > 0) {
        // Join paragraph lines with <br> and wrap in <p>
        processedLines.push('<p>' + currentParagraphLines.join('<br>') + '</p>');
        currentParagraphLines = [];
      }
    };

    const flushCurrentTable = () => {
      if (currentTableHeaders === null) { return; }
      const thead = '<thead><tr>'
        + currentTableHeaders.map(h => `<th>${h}</th>`).join('')
        + '</tr></thead>';
      const tbody = currentTableRows.length
        ? '<tbody>' + currentTableRows.map(r =>
            '<tr>' + r.map(c => `<td>${c}</td>`).join('') + '</tr>'
          ).join('') + '</tbody>'
        : '';
      // Wrap the table in an overflow-x container so the CSS can give
      // it a single enclosing border + horizontal scroll on wide
      // content. Without the wrapper, applying overflow to <table>
      // requires display:block which breaks auto column sizing.
      processedLines.push('<div class="md-table-wrap"><table>' + thead + tbody + '</table></div>');
      currentTableHeaders = null;
      currentTableRows = [];
    };

    // Helper to split a markdown table row by `|`, dropping the leading/trailing
    // empty cells from the surrounding pipes and trimming each cell.
    const splitRow = (line: string): string[] => {
      const parts = line.split('|').map(c => c.trim());
      // drop leading and trailing empties from the outer pipes
      if (parts.length && parts[0] === '') { parts.shift(); }
      if (parts.length && parts[parts.length - 1] === '') { parts.pop(); }
      return parts;
    };

    // Detects the divider line under a table header: `|---|---|` etc.
    const isTableDivider = (line: string): boolean => {
      if (!line.includes('|')) { return false; }
      const cells = splitRow(line);
      return cells.length > 0 && cells.every(c => /^:?-{3,}:?$/.test(c));
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // ---------- TABLE DETECTION ----------
      // A markdown table is: header row (| ... |) followed immediately by a
      // divider row (|---|---|), then 0+ data rows.
      const looksLikeTableRow = trimmedLine.startsWith('|') && trimmedLine.endsWith('|');
      if (looksLikeTableRow && currentTableHeaders === null) {
        const next = (lines[i + 1] ?? '').trim();
        if (isTableDivider(next)) {
          flushCurrentParagraph();
          flushCurrentList();
          currentTableHeaders = splitRow(trimmedLine);
          i++; // skip divider
          continue;
        }
      } else if (looksLikeTableRow && currentTableHeaders !== null) {
        currentTableRows.push(splitRow(trimmedLine));
        continue;
      } else if (currentTableHeaders !== null) {
        // first non-table line ends the table
        flushCurrentTable();
      }

      // Check if this is a header line (already converted to HTML)
      const isHeader = /^<h[1-6]>/.test(trimmedLine);

      // Check for numbered list item (1. 2. 3. etc.) - convert to unordered list
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        flushCurrentParagraph();
        if (currentListType !== 'unordered') {
          flushCurrentList();
          currentListType = 'unordered';
        }
        currentListItems.push(`<li>${numberedMatch[2]}</li>`);
        continue;
      }

      // Check for bullet list item (- or *)
      const bulletMatch = trimmedLine.match(/^[-*]\s+(.+)$/);
      if (bulletMatch) {
        flushCurrentParagraph();
        if (currentListType !== 'unordered') {
          flushCurrentList();
          currentListType = 'unordered';
        }
        currentListItems.push(`<li>${bulletMatch[1]}</li>`);
        continue;
      }

      // Skip empty lines if we're currently building a list (don't break the list)
      if (trimmedLine === '' && currentListType !== null) {
        continue;
      }

      // If we get here, it's not a list item, so flush any current list
      flushCurrentList();

      // Handle headers - they go directly without paragraph wrapping
      if (isHeader) {
        flushCurrentParagraph();
        processedLines.push(trimmedLine);
        continue;
      }

      // Handle empty lines - flush paragraph and add spacing
      if (trimmedLine === '') {
        flushCurrentParagraph();
        continue;
      }

      // Regular text - accumulate into current paragraph
      currentParagraphLines.push(trimmedLine);
    }

    // Flush any remaining content
    flushCurrentList();
    flushCurrentParagraph();
    flushCurrentTable();

    // Join processed lines (already structured with proper HTML tags)
    formatted = processedLines.join('')
      // Multiple spaces -> non-breaking spaces for better formatting
      .replace(/\s{2,}/g, (match) => '&nbsp;'.repeat(match.length));

    return formatted;
  }
}
